import { Navigate, Route, Routes } from "react-router-dom";
import { AdminLayout } from "./components/AdminLayout";
import { AffiliateLayout } from "./components/AffiliateLayout";
import { AppLayout } from "./components/AppLayout";
import { CompanyTypeRoute } from "./components/CompanyTypeRoute";
import { ConsultancyLayout } from "./components/ConsultancyLayout";
import { HrLayout } from "./components/HrLayout";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { RoleRoute } from "./components/RoleRoute";
import { AdminApplicationsPage } from "./pages/admin/AdminApplicationsPage";
import { AdminSubmissionDetailPage } from "./pages/admin/AdminSubmissionDetailPage";
import { AdminClientInvoicesPage } from "./pages/admin/AdminClientInvoicesPage";
import { AdminDashboardPage } from "./pages/admin/AdminDashboardPage";
import { AdminDisputesPage } from "./pages/admin/AdminDisputesPage";
import { AdminInvoiceDetailPage } from "./pages/admin/AdminInvoiceDetailPage";
import { AdminJobDetailPage } from "./pages/admin/AdminJobDetailPage";
import { AdminJobsPage } from "./pages/admin/AdminJobsPage";
import { AdminPayoutsPage } from "./pages/admin/AdminPayoutsPage";
import { AdminSkillsPage } from "./pages/admin/AdminSkillsPage";
import { AdminLogsPage } from "./pages/admin/AdminLogsPage";
import { EmailCampaignsPage } from "./pages/admin/EmailCampaignsPage";
import { CompaniesPage } from "./pages/admin/CompaniesPage";
import { AdminCompanyContractsPage } from "./pages/admin/AdminCompanyContractsPage";
import { AdminConsultancyRateCardsPage } from "./pages/admin/AdminConsultancyRateCardsPage";
import { AdminProfilePage } from "./pages/admin/AdminProfilePage";
import { AdminPeoplePage } from "./pages/admin/AdminPeoplePage";
import { AdminRecruiterDetailPage } from "./pages/admin/AdminRecruiterDetailPage";
import { AdminAffiliateDetailPage } from "./pages/admin/AdminAffiliateDetailPage";
import { RecruitersPage } from "./pages/admin/RecruitersPage";
import { DisputeDetailPage } from "./pages/DisputeDetailPage";
import { DisputesPage } from "./pages/DisputesPage";
import { ApplyPage } from "./pages/ApplyPage";
import { SystemApplyPage } from "./pages/SystemApplyPage";
import { ApplyErrorBoundary } from "./components/ApplyErrorBoundary";
import { AddCandidatePage } from "./pages/AddCandidatePage";
import { BulkUploadPage } from "./pages/BulkUploadPage";
import { CandidatesPage } from "./pages/CandidatesPage";
import { DashboardPage } from "./pages/DashboardPage";
import { ExplorePage } from "./pages/ExplorePage";
import { AffiliateDashboardPage } from "./pages/affiliate/AffiliateDashboardPage";
import { AffiliateJobsPage } from "./pages/affiliate/AffiliateJobsPage";
import { AffiliatePaymentsPage } from "./pages/affiliate/AffiliatePaymentsPage";
import { AffiliateProfilePage } from "./pages/affiliate/AffiliateProfilePage";
import { HrDashboardPage } from "./pages/hr/HrDashboardPage";
import { HrContractPage } from "./pages/hr/HrContractPage";
import { HrJobDetailPage } from "./pages/hr/HrJobDetailPage";
import { HrJobsPage } from "./pages/hr/HrJobsPage";
import { HrSubmissionDetailPage } from "./pages/hr/HrSubmissionDetailPage";
import { HrSubmissionsPage } from "./pages/hr/HrSubmissionsPage";
import { HrTemplatesPage } from "./pages/hr/HrTemplatesPage";
import { HrTeamPage } from "./pages/hr/HrTeamPage";
import { ConsultancyDashboardPage } from "./pages/consultancy/ConsultancyDashboardPage";
import { ConsultancyRateCardPage } from "./pages/consultancy/ConsultancyRateCardPage";
import { ConsultancyRecruitersPage } from "./pages/consultancy/ConsultancyRecruitersPage";
import { ConsultancyTeamPage } from "./pages/consultancy/ConsultancyTeamPage";
import { CompanyKycPage } from "./pages/CompanyKycPage";
import { HrIdentityKycPage } from "./pages/hr/HrIdentityKycPage";
import { NAV_COPY, NAV_ICONS } from "./lib/navCopy";
import { adminApi, consultancyApi } from "./lib/api";
import {
  CAMPAIGN_CANDIDATES_EMPTY_DESCRIPTION,
  CAMPAIGN_CANDIDATES_EMPTY_TITLE,
  CAMPAIGN_CANDIDATES_SUBTITLE,
  campaignCandidatesSubtitle,
} from "./constants/matching";
import { JobWizardPage } from "./pages/hr/JobWizardPage";
import { JobDetailPage } from "./pages/JobDetailPage";
import { JobsPage } from "./pages/JobsPage";
import { KycPage } from "./pages/KycPage";
import { LoginPage } from "./pages/LoginPage";
import { AppleCallbackPage } from "./pages/auth/AppleCallbackPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { JoinHubPage } from "./pages/join/JoinHubPage";
import { JoinAffiliatePage } from "./pages/join/JoinAffiliatePage";
import { JoinCompanyPage } from "./pages/join/JoinCompanyPage";
import { JoinRecruiterPage } from "./pages/join/JoinRecruiterPage";
import { PaymentsPage } from "./pages/PaymentsPage";
import { ProfilePage } from "./pages/ProfilePage";
import { AccountSettingsPage } from "./pages/account/AccountSettingsPage";
import { DeleteAccountPage } from "./pages/account/DeleteAccountPage";
import { CandidateProfilePage } from "./pages/profile/CandidateProfilePage";
import { UserProfilePage } from "./pages/profile/UserProfilePage";
import { SubmissionDetailPage } from "./pages/SubmissionDetailPage";
import { SubmissionsPage } from "./pages/SubmissionsPage";

function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/apple/callback" element={<AppleCallbackPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/register" element={<Navigate to="/join" replace />} />
      <Route path="/join" element={<JoinHubPage />} />
      <Route path="/join/recruiter" element={<JoinRecruiterPage />} />
      <Route path="/join/affiliate" element={<JoinAffiliatePage />} />
      <Route path="/join/company" element={<JoinCompanyPage companyType="client" />} />
      <Route path="/join/consultancy" element={<JoinCompanyPage companyType="consultancy" />} />
      <Route path="/join/hr" element={<Navigate to="/join/company" replace />} />
      <Route path="/join/pending" element={<Navigate to="/join" replace />} />
      <Route path="/admin/access-requests" element={<Navigate to="/admin/companies/clients" replace />} />
      <Route
        path="/apply"
        element={
          <ApplyErrorBoundary>
            <SystemApplyPage />
          </ApplyErrorBoundary>
        }
      />

      <Route
        path="/apply/:token"
        element={
          <ApplyErrorBoundary>
            <ApplyPage />
          </ApplyErrorBoundary>
        }
      />

      <Route element={<ProtectedRoute />}>
        <Route path="/account/delete" element={<DeleteAccountPage />} />
        <Route element={<RoleRoute allowedRoles={["admin"]} />}>
          <Route element={<AdminLayout />}>
            <Route path="/admin" element={<AdminDashboardPage />} />
            <Route path="/admin/disputes" element={<AdminDisputesPage />} />
            <Route path="/admin/disputes/:disputeId" element={<DisputeDetailPage basePath="/admin/disputes" admin />} />
            <Route path="/admin/recruiters" element={<RecruitersPage />} />
            <Route path="/admin/recruiters/:recruiterId" element={<AdminRecruiterDetailPage />} />
            <Route path="/admin/affiliates/:affiliateId" element={<AdminAffiliateDetailPage />} />
            <Route path="/admin/companies/clients" element={<CompaniesPage key="client" companyType="client" />} />
            <Route path="/admin/companies/consultancies" element={<CompaniesPage key="consultancy" companyType="consultancy" />} />
            <Route path="/admin/companies" element={<Navigate to="/admin/companies/clients" replace />} />
            <Route path="/admin/companies/:companyId/contracts" element={<AdminCompanyContractsPage />} />
            <Route path="/admin/companies/:companyId/rate-cards" element={<AdminConsultancyRateCardsPage />} />
            <Route path="/admin/applications" element={<AdminApplicationsPage />} />
            <Route path="/admin/submissions/:submissionId" element={<AdminSubmissionDetailPage />} />
            <Route path="/admin/jobs" element={<AdminJobsPage />} />
            <Route path="/admin/jobs/:jobId" element={<AdminJobDetailPage />} />
            <Route path="/admin/skills" element={<AdminSkillsPage />} />
            <Route path="/admin/logs" element={<AdminLogsPage />} />
            <Route path="/admin/email-campaigns" element={<EmailCampaignsPage />} />
            <Route
              path="/admin/candidates"
              element={
                <CandidatesPage
                  basePath="/admin/candidates"
                  submissionsBasePath="/admin/submissions"
                  listCandidates={adminApi.listCampaignCandidates}
                  title={NAV_COPY.admin.campaignCandidates}
                  subtitle={CAMPAIGN_CANDIDATES_SUBTITLE}
                  formatSubtitle={campaignCandidatesSubtitle}
                  emptyTitle={CAMPAIGN_CANDIDATES_EMPTY_TITLE}
                  emptyDescription={CAMPAIGN_CANDIDATES_EMPTY_DESCRIPTION}
                  showAddCandidate={false}
                  showChannelColumn
                />
              }
            />
            <Route path="/admin/finance/invoices" element={<AdminClientInvoicesPage />} />
            <Route path="/admin/finance/invoices/:invoiceId" element={<AdminInvoiceDetailPage />} />
            <Route path="/admin/finance/payouts" element={<AdminPayoutsPage />} />
            <Route path="/admin/people" element={<Navigate to="/admin/recruiters" replace />} />
            <Route
              path="/admin/affiliates"
              element={
                <AdminPeoplePage
                  role="affiliate"
                  title={NAV_COPY.common.affiliates}
                  subtitle="Affiliate accounts that share jobs and earn a cut."
                  icon={NAV_ICONS.affiliates}
                />
              }
            />
            <Route
              path="/admin/people/hr"
              element={
                <AdminPeoplePage
                  role="company_user"
                  companyType="client"
                  title={NAV_COPY.admin.clientHr}
                  subtitle="Hiring users at client companies."
                  icon={NAV_ICONS.clientHr}
                />
              }
            />
            <Route
              path="/admin/people/consultancy"
              element={
                <AdminPeoplePage
                  role="company_user"
                  companyType="consultancy"
                  title={NAV_COPY.admin.consultancyAdmins}
                  subtitle="Admin users at consultancy organisations."
                  icon={NAV_ICONS.consultancyAdmins}
                />
              }
            />
            <Route
              path="/admin/people/admins"
              element={
                <AdminPeoplePage role="admin" title={NAV_COPY.common.admins} subtitle="Internal AxioHire operators." icon={NAV_ICONS.admins} />
              }
            />
            <Route path="/admin/people/:userId" element={<UserProfilePage />} />
            <Route path="/admin/candidates/:candidateId" element={<CandidateProfilePage />} />
            <Route path="/admin/profile" element={<AdminProfilePage />} />
          </Route>
        </Route>

        <Route element={<RoleRoute allowedRoles={["company_user"]} />}>
          <Route element={<CompanyTypeRoute allowedType="client" />}>
            <Route element={<HrLayout />}>
              <Route path="/hr" element={<HrDashboardPage />} />
              <Route path="/hr/kyc" element={<CompanyKycPage companyType="client" />} />
              <Route path="/hr/identity" element={<HrIdentityKycPage />} />
              <Route path="/hr/profile" element={<AccountSettingsPage />} />
              <Route path="/hr/jobs" element={<HrJobsPage />} />
              <Route path="/hr/jobs/new" element={<JobWizardPage />} />
              <Route path="/hr/jobs/:jobId/edit" element={<JobWizardPage />} />
              <Route path="/hr/jobs/:jobId" element={<HrJobDetailPage />} />
              <Route path="/hr/templates" element={<HrTemplatesPage />} />
              <Route path="/hr/contract" element={<HrContractPage />} />
              <Route path="/hr/team" element={<HrTeamPage />} />
              <Route path="/hr/submissions" element={<HrSubmissionsPage />} />
              <Route path="/hr/submissions/:submissionId" element={<HrSubmissionDetailPage />} />
              <Route path="/hr/candidates/:candidateId" element={<CandidateProfilePage />} />
              <Route path="/hr/disputes" element={<DisputesPage basePath="/hr/disputes" />} />
              <Route path="/hr/disputes/:disputeId" element={<DisputeDetailPage basePath="/hr/disputes" />} />
            </Route>
          </Route>
          <Route element={<CompanyTypeRoute allowedType="consultancy" />}>
            <Route element={<ConsultancyLayout />}>
              <Route path="/consultancy" element={<ConsultancyDashboardPage />} />
              <Route path="/consultancy/kyc" element={<CompanyKycPage companyType="consultancy" />} />
              <Route path="/consultancy/jobs" element={<JobsPage basePath="/consultancy/jobs" listJobs={consultancyApi.listJobs} showEarnShare />} />
              <Route path="/consultancy/jobs/:jobId" element={<JobDetailPage readOnly />} />
              <Route
                path="/consultancy/candidates"
                element={
                  <CandidatesPage
                    basePath="/consultancy/candidates"
                    submissionsBasePath="/consultancy/submissions"
                    listCandidates={consultancyApi.listCandidates}
                    title="Candidates"
                    subtitle="All candidates in your consultancy pool."
                    showAddCandidate={false}
                    showRecruiterColumn
                  />
                }
              />
              <Route path="/consultancy/candidates/:candidateId" element={<CandidateProfilePage />} />
              <Route
                path="/consultancy/submissions"
                element={
                  <SubmissionsPage
                    basePath="/consultancy/submissions"
                    listSubmissions={consultancyApi.listSubmissions}
                    title="Submissions"
                    subtitle="Pipeline across all of your recruiters."
                    showRecruiterColumn
                  />
                }
              />
              <Route
                path="/consultancy/submissions/:submissionId"
                element={<SubmissionDetailPage basePath="/consultancy/submissions" jobsBasePath="/consultancy/jobs" readOnly />}
              />
              <Route
                path="/consultancy/payments"
                element={
                  <PaymentsPage
                    summaryFn={consultancyApi.earnings}
                    payoutsFn={consultancyApi.payouts}
                    submissionBasePath="/consultancy/submissions"
                    allowDispute={false}
                    title="Payments"
                    subtitle="Payouts for placements made by your consultancy."
                    redirectAffiliatedRecruiters={false}
                  />
                }
              />
              <Route path="/consultancy/recruiters" element={<ConsultancyRecruitersPage />} />
              <Route path="/consultancy/team" element={<ConsultancyTeamPage />} />
              <Route path="/consultancy/rate-card" element={<ConsultancyRateCardPage />} />
              <Route path="/consultancy/profile" element={<AccountSettingsPage />} />
            </Route>
          </Route>
        </Route>

        <Route element={<RoleRoute allowedRoles={["recruiter", "affiliate"]} />}>
          <Route path="/kyc" element={<KycPage />} />
        </Route>

        <Route element={<RoleRoute allowedRoles={["recruiter"]} />}>
          <Route element={<AppLayout />}>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/explore" element={<ExplorePage />} />
            <Route path="/jobs" element={<JobsPage />} />
            <Route path="/jobs/:jobId" element={<JobDetailPage />} />
            <Route path="/candidates" element={<CandidatesPage />} />
            <Route path="/candidates/new" element={<AddCandidatePage />} />
            <Route path="/candidates/bulk-upload" element={<BulkUploadPage />} />
            <Route path="/candidates/:candidateId" element={<CandidateProfilePage />} />
            <Route path="/submissions" element={<SubmissionsPage />} />
            <Route path="/submissions/:submissionId" element={<SubmissionDetailPage />} />
            <Route path="/disputes" element={<DisputesPage />} />
            <Route path="/disputes/:disputeId" element={<DisputeDetailPage />} />
            <Route path="/payments" element={<PaymentsPage />} />
            <Route path="/profile" element={<ProfilePage />} />
          </Route>
        </Route>

        <Route element={<RoleRoute allowedRoles={["affiliate"]} />}>
          <Route element={<AffiliateLayout />}>
            <Route path="/affiliate" element={<AffiliateDashboardPage />} />
            <Route path="/affiliate/jobs" element={<AffiliateJobsPage />} />
            <Route path="/affiliate/payments" element={<AffiliatePaymentsPage />} />
            <Route path="/affiliate/profile" element={<AffiliateProfilePage />} />
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

export default App;
